import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi'
import zxcvbn from 'zxcvbn'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { copy } from './Slide4MasterKey.copy'

const ease = [0.16, 1, 0.3, 1] as const

// Toggle the interactive password checker inside the tips panel.
// Hidden for now while we decide if it belongs here or only on the tools page.
const SHOW_PASSWORD_CHECKER = false

// Curated list of friendly everyday words for the three-word passphrase
// example. No sensitive, political, or embarrassing terms. Kept inline so
// the generator has no runtime dependencies. Latin script in every language:
// passwords are typed in Latin characters on practically every service.
const PASSPHRASE_WORDS = [
  'acorn', 'amber', 'anchor', 'apple', 'autumn', 'badger', 'balloon', 'basket',
  'beaver', 'berry', 'biscuit', 'blanket', 'blossom', 'bottle', 'boulder', 'branch',
  'breeze', 'butter', 'butterfly', 'canary', 'candle', 'canvas', 'canyon', 'cardinal',
  'carpet', 'cedar', 'cherry', 'cliff', 'clock', 'clover', 'compass', 'cookie',
  'copper', 'coral', 'cottage', 'cream', 'creek', 'crystal', 'cushion', 'daisy',
  'dawn', 'deer', 'desert', 'dolphin', 'dove', 'drawer', 'dune', 'dusk',
  'eagle', 'ember', 'emerald', 'evening', 'falcon', 'feather', 'fern', 'finch',
  'forest', 'fox', 'frost', 'garden', 'golden', 'guitar', 'hare', 'harbor',
  'heron', 'hill', 'honey', 'indigo', 'island', 'ivory', 'ivy', 'kettle',
  'ladder', 'lagoon', 'lake', 'lantern', 'lavender', 'leaf', 'lemon', 'lilac',
  'lion', 'lynx', 'maple', 'marble', 'meadow', 'midnight', 'mirror', 'mist',
  'moose', 'morning', 'moss', 'mountain', 'muffin', 'noodle', 'ocean', 'olive',
  'orange', 'orchard', 'otter', 'owl', 'panda', 'peach', 'pebble', 'pelican',
  'pencil', 'penguin', 'pepper', 'petal', 'piano', 'pillow', 'pine', 'plum',
  'pond', 'pumpkin', 'purple', 'rabbit', 'rain', 'rainbow', 'ribbon', 'river',
  'robin', 'ruby', 'saddle', 'sand', 'scarlet', 'seal', 'shelf', 'shore',
  'silver', 'sky', 'sparrow', 'spring', 'squirrel', 'stag', 'stone', 'sugar',
  'summer', 'sunset', 'swan', 'table', 'teal', 'thunder', 'tiger', 'toast',
  'toucan', 'trumpet', 'turtle', 'twilight', 'umbrella', 'valley', 'velvet', 'violet',
  'violin', 'waffle', 'wagon', 'walnut', 'whale', 'willow', 'window', 'winter', 'wolf',
]

function pickThreeWords(): string[] {
  const pool = [...PASSPHRASE_WORDS]
  const picks: string[] = []
  for (let i = 0; i < 3; i++) {
    const idx = Math.floor(Math.random() * pool.length)
    picks.push(pool[idx])
    pool.splice(idx, 1)
  }
  return picks
}

// Bar styling per zxcvbn score; the label comes from copy.strengthLabels.
const strengthConfig = [
  { barColor: 'bg-rose-500', textColor: 'text-rose-600', width: 'w-1/5' },
  { barColor: 'bg-orange-500', textColor: 'text-orange-600', width: 'w-2/5' },
  { barColor: 'bg-amber-500', textColor: 'text-amber-600', width: 'w-3/5' },
  { barColor: 'bg-emerald-500', textColor: 'text-emerald-600', width: 'w-4/5' },
  { barColor: 'bg-emerald-600', textColor: 'text-emerald-700', width: 'w-full' },
]

// Diagram geometry for the three services; labels come from copy.serviceLabels.
const SERVICES = [
  { id: 'email', y: 40, labelOffset: -1 },
  { id: 'banking', y: 88, labelOffset: -4 },
  { id: 'shopping', y: 136, labelOffset: -6 },
]

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '')
}

export default function Slide4MasterKey(_props: SlideProps) {
  const { isActive } = _props
  const c = useSlideCopy(copy)
  const isAr = useLocale() === 'ar'
  // The key diagram is drawn in physical LTR coordinates (SVG lines plus
  // absolute offsets), then mirrored as a whole under RTL via `dir-flip`.
  // Text-bearing children carry `dir-flip` again to read normally, and take
  // the visitor's direction so labels hug the arrow tips.
  const textDir = isAr ? 'rtl' : 'ltr'
  const offscreen = isAr ? '-100%' : '100%'
  const [guideOpen, setGuideOpen] = useState(false)
  const [threeWords, setThreeWords] = useState<string[]>(() => pickThreeWords())
  const [pw, setPw] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [hibpCount, setHibpCount] = useState<number | null>(null)
  const [hibpChecking, setHibpChecking] = useState(false)

  const result = pw ? zxcvbn(pw) : null
  const strength = result ? strengthConfig[result.score] : null
  const strengthLabel = result ? c.strengthLabels[result.score] : null
  const crackTime = result?.crack_times_display?.offline_slow_hashing_1e4_per_second as
    | string
    | undefined

  useEffect(() => {
    if (!pw) {
      setHibpCount(null)
      return
    }

    const timeout = setTimeout(async () => {
      try {
        setHibpChecking(true)
        const encoder = new TextEncoder()
        const data = encoder.encode(pw)
        const hashBuffer = await crypto.subtle.digest('SHA-1', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase()
        const prefix = hashHex.slice(0, 5)
        const suffix = hashHex.slice(5)

        const resp = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`)
        const text = await resp.text()
        const match = text.split('\n').find((line) => line.startsWith(suffix))
        setHibpCount(match ? parseInt(match.split(':')[1].trim(), 10) : 0)
      } catch {
        setHibpCount(null)
      } finally {
        setHibpChecking(false)
      }
    }, 500)

    return () => clearTimeout(timeout)
  }, [pw])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.05, ease }}
        className="mb-8 w-full max-w-[540px]"
      >
        <div dir="ltr" className="dir-flip relative h-[170px] overflow-hidden rounded-[28px] bg-white">
          <div className="relative h-full w-full">
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 6 }}
                animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
                transition={{ duration: 0.35, delay: 1.05 + index * 0.8, ease }}
                className="absolute start-[336px] -translate-y-1/2"
                style={{ top: `${service.y}px` }}
              >
                <div
                  dir={textDir}
                  className="dir-flip flex w-36 items-center gap-2 ps-2 text-start text-base font-medium text-slate-600"
                  style={{ marginTop: `${service.labelOffset}px` }}
                >
                  <span>{c.serviceLabels[index]}</span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={isActive ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.2, delay: 5.34 + index * 0.8, ease }}
                    className="inline-flex items-center justify-center text-emerald-500"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <motion.path
                        d="M3 8.25L6.2 11.35L13 4.65"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={isActive ? { pathLength: 1 } : { pathLength: 0 }}
                        transition={{ duration: 0.32, delay: 5.38 + index * 0.8, ease }}
                      />
                    </svg>
                  </motion.span>
                </div>
              </motion.div>
            ))}

            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 540 176" fill="none" aria-hidden="true">
              <defs>
                <marker
                  id="password-line-arrow"
                  markerWidth="8"
                  markerHeight="8"
                  refX="6"
                  refY="4"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0 1.5L6 4L0 6.5V1.5Z" fill="#94a3b8" />
                </marker>
              </defs>
              <motion.g
                initial={{ opacity: 1 }}
                animate={isActive ? { opacity: [1, 1, 0] } : { opacity: 0 }}
                transition={{ duration: 1.1, times: [0, 0.55, 1], delay: 3.45, ease }}
              >
                {SERVICES.map((service, index) => (
                  <motion.line
                    key={`bad-${service.id}`}
                    x1="118"
                    y1="40"
                    x2="118"
                    y2="40"
                    stroke="#94a3b8"
                    strokeWidth="2"
                    strokeDasharray="8 8"
                    strokeLinecap="round"
                    markerEnd="url(#password-line-arrow)"
                    initial={{ opacity: 0 }}
                    animate={
                      isActive
                        ? { x2: 336, y2: service.y, opacity: 1 }
                        : { x2: 118, y2: 40, opacity: 0 }
                    }
                    transition={{
                      duration: 0.6,
                      delay: 0.42 + index * 0.8,
                      ease,
                    }}
                  />
                ))}
              </motion.g>

              {SERVICES.map((service, index) => (
                <motion.line
                  key={`good-${service.id}`}
                  x1="118"
                  y1={service.y}
                  x2="118"
                  y2={service.y}
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeDasharray="8 8"
                  strokeLinecap="round"
                  markerEnd="url(#password-line-arrow)"
                  initial={{ opacity: 0 }}
                  animate={
                    isActive
                      ? { x2: 336, opacity: 1 }
                      : { x2: 118, opacity: 0 }
                  }
                  transition={{ duration: 0.6, delay: 4.7 + index * 0.8, ease }}
                />
              ))}
            </svg>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.45, delay: 0.22, ease }}
              className="absolute start-[90px] top-0 -translate-x-1/2 -translate-y-1/2"
              style={{ top: `${SERVICES[0].y}px` }}
            >
              <div className="relative">
                <svg className="h-7 w-7 text-slate-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M12.59 2a4.42 4.42 0 00-4.245 5.656l-5.888 5.888a.75.75 0 00-.22.53V17A1 1 0 003.25 18H6a.75.75 0 00.75-.75V16h1.25a.75.75 0 00.75-.75V14h1.25a.75.75 0 00.53-.22l1.814-1.814A4.42 4.42 0 1012.59 2zm0 1.5a2.92 2.92 0 110 5.84 2.92 2.92 0 010-5.84z" />
                </svg>
                <span className="dir-flip absolute -end-1 -top-1 text-[10px] font-semibold leading-none text-slate-500">
                  1
                </span>
              </div>
            </motion.div>

            {SERVICES.slice(1).map((service, index) => (
              <motion.div
                key={`good-key-${service.id}`}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.97 }}
                transition={{ duration: 1.05, delay: 5.12 + index * 0.8, ease }}
                className="absolute start-[90px] top-0 -translate-x-1/2 -translate-y-1/2"
                style={{ top: `${service.y}px` }}
              >
                <div className="relative">
                  <svg className="h-6 w-6 text-slate-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M12.59 2a4.42 4.42 0 00-4.245 5.656l-5.888 5.888a.75.75 0 00-.22.53V17A1 1 0 003.25 18H6a.75.75 0 00.75-.75V16h1.25a.75.75 0 00.75-.75V14h1.25a.75.75 0 00.53-.22l1.814-1.814A4.42 4.42 0 1012.59 2zm0 1.5a2.92 2.92 0 110 5.84 2.92 2.92 0 010-5.84z" />
                  </svg>
                  <span className="dir-flip absolute -end-1 -top-1 text-[10px] font-semibold leading-none text-slate-500">
                    {index + 2}
                  </span>
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={isActive ? { opacity: [0, 1, 1, 0], scale: [0.85, 1, 1, 1] } : { opacity: 0, scale: 0.85 }}
              transition={{ duration: 1.45, times: [0, 0.16, 0.84, 1], delay: 2.95, ease }}
              className="absolute left-1/2 top-[44%] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/70 p-3 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
            >
              <svg className="h-16 w-16 text-rose-500/90" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M4.47 4.47a.75.75 0 011.06 0L10 8.94l4.47-4.47a.75.75 0 111.06 1.06L11.06 10l4.47 4.47a.75.75 0 01-1.06 1.06L10 11.06l-4.47 4.47a.75.75 0 01-1.06-1.06L8.94 10 4.47 5.53a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15, ease }}
        className="text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] text-balance"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-3xl"
      >
        {c.body}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.tipsButton}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </motion.button>

      {/* Slideover */}
      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-s border-slate-200 bg-white text-start shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M8 7a5 5 0 113.61 4.804l-1.903 1.903A1 1 0 019 14H8v1a1 1 0 01-1 1H6v1a1 1 0 01-1 1H3a1 1 0 01-1-1v-2a1 1 0 01.293-.707L8.196 8.39A5.002 5.002 0 018 7zm5-3a.75.75 0 000 1.5A1.5 1.5 0 0114.5 7 .75.75 0 0016 7a3 3 0 00-3-3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                        {c.guideTitle}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setGuideOpen(false)}
                    aria-label={c.closeAria}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <div className="space-y-7">
                  <section className="pb-2">
                    <h4 className="text-base font-semibold text-slate-900">
                      {c.tip1Heading}
                    </h4>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">
                      {c.tip1Body}
                    </p>

                    <div className="mt-4 ms-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-slate-500">{c.exampleLabel}</p>
                        <button
                          type="button"
                          onClick={() => setThreeWords(pickThreeWords())}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 cursor-pointer"
                          aria-label={c.tryAnotherAria}
                        >
                          <FiRefreshCw className="h-3 w-3" />
                          {c.tryAnother}
                        </button>
                      </div>
                      <div className="mt-3 min-h-[1.75rem]">
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.p
                            key={threeWords.join('-')}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.2, ease }}
                            className="font-mono text-xl font-semibold text-slate-900 tabular-nums"
                          >
                            {threeWords.join('•')}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                    </div>

                    {SHOW_PASSWORD_CHECKER && (
                      <div className="mt-6 ph-no-capture" data-testid="pw-checker">
                        <label className="text-sm text-slate-500">
                          {c.checkLabel}
                        </label>
                        <div className="relative mt-3">
                          <input
                            type={showPw ? 'text' : 'password'}
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder={c.checkPlaceholder}
                            autoComplete="off"
                            spellCheck={false}
                            aria-label={c.checkInputAria}
                            data-ph-no-capture="true"
                            className="ph-no-capture w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pe-11 font-mono text-base text-slate-900 placeholder:font-sans placeholder:text-slate-300 focus:outline-none focus:border-slate-300 focus:ring-2 focus:ring-slate-100 transition"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPw((v) => !v)}
                            aria-label={showPw ? c.hidePassword : c.showPassword}
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            {showPw ? (
                              <FiEyeOff className="h-4 w-4" />
                            ) : (
                              <FiEye className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        {strength && (
                          <div className="mt-3 flex items-center justify-between text-sm">
                            <span className={`font-semibold ${strength.textColor}`}>
                              {strengthLabel}
                            </span>
                            {crackTime && (
                              <span className="text-slate-400">
                                {fill(c.crackTime, { time: crackTime })}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          {strength && (
                            <motion.div
                              initial={false}
                              animate={{
                                width: `${((result?.score ?? 0) + 1) * 20}%`,
                              }}
                              transition={{ duration: 0.35, ease }}
                              className={`h-full ${strength.barColor}`}
                            />
                          )}
                        </div>

                        <div className="mt-3 min-h-[52px]">
                          {!pw ? (
                            <p className="text-xs text-slate-400 leading-relaxed">
                              {c.privacyNote}
                            </p>
                          ) : hibpChecking ? (
                            <p className="text-xs text-slate-400">
                              {c.checking}
                            </p>
                          ) : hibpCount !== null && hibpCount > 0 ? (
                            <div className="rounded-lg bg-rose-50 border border-rose-100 px-3.5 py-2.5">
                              <p className="text-sm font-semibold text-rose-700">
                                {fill(hibpCount === 1 ? c.foundOne : c.foundMany, {
                                  count: hibpCount.toLocaleString(),
                                })}
                              </p>
                              <p className="mt-0.5 text-xs text-rose-600/80 leading-relaxed">
                                {c.foundNote}
                              </p>
                            </div>
                          ) : hibpCount === 0 ? (
                            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-2.5">
                              <p className="text-sm font-semibold text-emerald-700">
                                {c.notFound}
                              </p>
                              <p className="mt-0.5 text-xs text-emerald-700/80 leading-relaxed">
                                {c.notFoundNote}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </section>

                  <div className="space-y-5">
                    <section className="pb-2 last:pb-0">
                      <h4 className="text-base font-semibold text-slate-900">
                        2. {c.tip2Heading}
                      </h4>
                      <p className="mt-2 text-base leading-relaxed text-slate-600">
                        {c.tip2Body}
                      </p>
                    </section>
                    <section className="pb-2 last:pb-0">
                      <h4 className="text-base font-semibold text-slate-900">
                        3. {c.tip3Heading}
                      </h4>
                      <p className="mt-2 text-base leading-relaxed text-slate-600">
                        {c.tip3Body}
                      </p>
                    </section>
                    <section className="pb-2 last:pb-0">
                      <h4 className="text-base font-semibold text-slate-900">
                        4. {c.tip4Heading}
                      </h4>
                      <p className="mt-2 text-base leading-relaxed text-slate-600">
                        {c.tip4Before}
                        <a
                          href="https://bitwarden.com/products/personal/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-violet-600 underline decoration-violet-200 underline-offset-2 transition-colors hover:text-violet-700 hover:decoration-violet-400"
                        >
                          {c.tip4Link}
                        </a>
                        {c.tip4After}
                      </p>
                    </section>
                  </div>
                  <div className="pt-1">
                    <p className="text-base font-semibold text-slate-900">
                      {c.startEmailHeading}
                    </p>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">
                      {c.startEmailBody}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
